import { supabase } from '../supabaseClient';
import type { TutorialKapitelId } from '../../tutorial/types';

/**
 * LERNZENTRUM — Datenzugriff (Tabellen `tutorial_kapitel`, `tutorial_fortschritt`).
 *
 * Die EINZIGE Stelle, an der das Lernzentrum mit der Datenbank redet. Die
 * Regeln daneben (Fortschrittsrechnung, Suche, Lernpfadsortierung) stehen in
 * `tutorialLogic.ts` und sind ohne Supabase pruefbar — Tests laufen hier ohne
 * jsdom, also bleibt der Container duenn und die Rechnung rein.
 *
 * ─── Warum alles fail-open ist ──────────────────────────────────────────────
 *
 * Die Migration (Auftrag A1) laeuft erst auf TEST, PROD folgt nach Go des
 * Betreibers. Bis dahin antwortet PostgREST mit `42P01` (Tabelle fehlt),
 * `PGRST205` (Schema-Cache kennt sie nicht) oder `PGRST202` (RPC fehlt). Kein
 * einziger dieser Faelle darf einen Fehler-Toast erzeugen oder werfen: das
 * Register liegt im Code, der Lernstoff ist also auch OHNE Datenbank
 * vollstaendig lesbar — es fehlen nur Videos, Screenshots und der gemerkte
 * Fortschritt. Eine Hilfe, die sich selbst mit einer roten Meldung begruesst,
 * ist schlimmer als eine Hilfe ohne Video.
 *
 * Dasselbe gilt fuers Schreiben, und das ist hier die Ausnahme von der
 * Hausregel „Lesen fail-open, Schreiben nicht": `markiereGesehen` ist eine
 * Randnotiz, kein Nachweis. Scheitert sie, hat der Nutzer trotzdem gelesen.
 * Sie meldet das per Rueckgabewert, nicht per Ausnahme.
 */

const T_KAPITEL = 'tutorial_kapitel';
const T_FORTSCHRITT = 'tutorial_fortschritt';
const T_BENUTZER_ROLLEN = 'benutzer_rollen';

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205; RPCs PGRST202. */
const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};

/**
 * Migration noch nicht gefahren — kein Fehler, nur „noch nichts da".
 *
 * Fuenf Codes, weil die Migration in Stufen ankommt:
 *  · `42P01`   — die Tabelle gibt es nicht.
 *  · `PGRST205`— es gibt sie, der Schema-Cache kennt sie noch nicht.
 *  · `PGRST202`— die RPC gibt es nicht.
 *  · `42703`   — die Tabelle steht, eine spaeter ergaenzte SPALTE fehlt noch
 *                (Teilstand zwischen zwei Migrationsnummern).
 *  · `23503`   — Fremdschluessel auf `tutorial_kapitel` verletzt: der
 *                Fortschritt soll auf ein Kapitel zeigen, das die Pipeline
 *                noch nicht geschrieben hat. Vor dem ersten Pipeline-Lauf ist
 *                das der Normalfall und kein Fehler des Nutzers.
 */
export const istNochNichtVeroeffentlicht = (e: unknown): boolean => {
    const code = errorCode(e);
    return (
        code === '42P01' ||
        code === 'PGRST205' ||
        code === 'PGRST202' ||
        code === '42703' ||
        code === '23503'
    );
};

// ─── Formen ──────────────────────────────────────────────────────────────────

/** Bucket-Pfade aus `tutorial_kapitel.medien` (Plan §1.7). Alles optional. */
export interface KapitelMedien {
    video?: string;
    poster?: string;
    vtt?: string;
    /** URL von `marken.json` — `[{schritt, startMs}]` (Plan §2.3, `videoLogik.kapitelmarken`). */
    marken?: string;
    /** Ein Bild je Schritt, gleiche Reihenfolge wie `schritte` im Register. */
    screenshots?: string[];
}

export interface KapitelFaq {
    frage: string;
    antwort: string;
}

/**
 * `tutorial_kapitel.inhalt` (Plan §3). Jedes Feld optional: die Pipeline
 * (Paket C/D) fuellt sie nach und nach, und die Kapitelseite faellt fuer jedes
 * fehlende Feld auf das Register zurueck.
 */
export interface KapitelInhalt {
    /** Ausformulierter Text je Schritt, gleiche Reihenfolge wie im Register. */
    schritte?: string[];
    /** Ausformuliertes Praxisbeispiel. Fehlt es, formt die Seite eines aus `praxis` im Register. */
    praxis?: string;
    stolperfallen?: string[];
    faq?: KapitelFaq[];
    sprechertext?: string;
}

export interface KapitelDaten {
    id: TutorialKapitelId;
    version: number;
    inhalt: KapitelInhalt;
    medien: KapitelMedien;
}

export interface Fortschritt {
    kapitelId: TutorialKapitelId;
    gesehenAm: string | null;
    abgeschlossenAm: string | null;
}

// ─── Umformung ───────────────────────────────────────────────────────────────

const alsText = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

const alsTextliste = (v: unknown): string[] | undefined =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x.trim()) : undefined;

const alsFaq = (v: unknown): KapitelFaq[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const raus = v
        .map((e) => {
            const o = e as { frage?: unknown; antwort?: unknown };
            const frage = alsText(o?.frage);
            const antwort = alsText(o?.antwort);
            return frage && antwort ? { frage, antwort } : null;
        })
        .filter((e): e is KapitelFaq => e !== null);
    return raus.length ? raus : undefined;
};

/**
 * `inhalt.schritte` darf beides sein: eine Liste von Strings oder eine Liste
 * von Objekten mit `text`. Die Pipeline schreibt die zweite Form (sie fuehrt
 * pro Schritt noch Zeitmarken mit), Handnachbesserungen die erste. Beide
 * landen hier als Liste von Saetzen — die Kapitelseite kennt nur eine Form.
 */
const alsSchritttexte = (v: unknown): string[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const raus = v.map((e) => {
        if (typeof e === 'string') return e;
        const o = e as { text?: unknown; stichwort?: unknown };
        return alsText(o?.text) ?? alsText(o?.stichwort) ?? '';
    });
    return raus.some((s) => s.trim()) ? raus : undefined;
};

const alsPraxis = (v: unknown): string | undefined => {
    if (typeof v === 'string') return alsText(v);
    const o = v as { text?: unknown; beispiel?: unknown } | null;
    return alsText(o?.text) ?? alsText(o?.beispiel);
};

const mapMedien = (v: unknown): KapitelMedien => {
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    return {
        video: alsText(o.video),
        poster: alsText(o.poster),
        vtt: alsText(o.vtt) ?? alsText(o.untertitel),
        marken: alsText(o.marken),
        screenshots: alsTextliste(o.screenshots),
    };
};

const mapInhalt = (v: unknown): KapitelInhalt => {
    const o = (v && typeof v === 'object' ? v : {}) as Record<string, unknown>;
    return {
        schritte: alsSchritttexte(o.schritte),
        praxis: alsPraxis(o.praxis),
        stolperfallen: alsTextliste(o.stolperfallen),
        faq: alsFaq(o.faq),
        sprechertext: alsText(o.sprechertext),
    };
};

/**
 * Admin-Nachbesserungen je Abschnitt (Plan §1.6) ueberleben die Regenerierung
 * und muessen deshalb NACH `inhalt` gelegt werden, nicht davor. Nur gesetzte
 * Felder ueberschreiben; ein leeres `overrides` aendert nichts.
 */
const mitOverrides = (inhalt: KapitelInhalt, roh: unknown): KapitelInhalt => {
    const o = mapInhalt(roh);
    return {
        schritte: o.schritte ?? inhalt.schritte,
        praxis: o.praxis ?? inhalt.praxis,
        stolperfallen: o.stolperfallen ?? inhalt.stolperfallen,
        faq: o.faq ?? inhalt.faq,
        sprechertext: o.sprechertext ?? inhalt.sprechertext,
    };
};

// ─── Lesen ───────────────────────────────────────────────────────────────────

/**
 * Alle freigegebenen Kapitelinhalte, als Map `kapitel-id → Inhalt/Medien`.
 *
 * Nur `status = 'freigegeben'` (Plan §1.6): Entwuerfe sind ungeprueft erzeugter
 * KI-Text und gehen nie an Nutzer. Die Rollenfilterung macht die RLS, nicht
 * dieser Client — was der Nutzer nicht sehen darf, kommt gar nicht erst an.
 *
 * Fehlt die Tabelle: leere Map, kein Wurf, keine Meldung.
 */
export const ladeKapitelInhalte = async (): Promise<Map<TutorialKapitelId, KapitelDaten>> => {
    const raus = new Map<TutorialKapitelId, KapitelDaten>();
    try {
        const { data, error } = await supabase
            .from(T_KAPITEL)
            .select('id, version, inhalt, medien, overrides')
            .eq('status', 'freigegeben');
        if (error) throw error;
        (data ?? []).forEach((row: Record<string, unknown>) => {
            const id = alsText(row.id);
            if (!id) return;
            raus.set(id, {
                id,
                version: typeof row.version === 'number' ? row.version : 1,
                inhalt: mitOverrides(mapInhalt(row.inhalt), row.overrides),
                medien: mapMedien(row.medien),
            });
        });
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) {
            // Netzfehler, RLS, abgelaufene Sitzung: ebenfalls kein Toast. Die
            // Konsole reicht — der Lernstoff steht im Register und ist da.
            console.warn('[lernzentrum] Kapitelinhalte nicht geladen', e);
        }
        return new Map();
    }
    return raus;
};

/**
 * Fortschritt EINES Nutzers, als Map `kapitel-id → Fortschritt`.
 *
 * Die `user_id` wird mitgegeben statt hier geholt: der Aufrufer (useLernzentrum)
 * hat das Profil ohnehin, und ein zweiter `auth.getUser()`-Rundlauf je Aufruf
 * ist reine Wartezeit. Ohne Id gibt es nichts zu laden — leere Map.
 */
export const ladeFortschritt = async (userId: string | null | undefined): Promise<Map<TutorialKapitelId, Fortschritt>> => {
    if (!userId) return new Map();
    const raus = new Map<TutorialKapitelId, Fortschritt>();
    try {
        const { data, error } = await supabase
            .from(T_FORTSCHRITT)
            .select('kapitel_id, gesehen_am, abgeschlossen_am')
            .eq('user_id', userId);
        if (error) throw error;
        (data ?? []).forEach((row: Record<string, unknown>) => {
            const kapitelId = alsText(row.kapitel_id);
            if (!kapitelId) return;
            raus.set(kapitelId, {
                kapitelId,
                gesehenAm: alsText(row.gesehen_am) ?? null,
                abgeschlossenAm: alsText(row.abgeschlossen_am) ?? null,
            });
        });
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Fortschritt nicht geladen', e);
        return new Map();
    }
    return raus;
};

/**
 * Die Schlüssel der fachlichen Rollen EINES Nutzers aus `benutzer_rollen`
 * (Rollensystem, Nr. 149) — für `rollenAusProfil`.
 *
 * Die RLS (`benutzer_rollen_select`) liefert nur Zuweisungen der eigenen
 * Firma (`current_company_id()`), also genau die Zeilen, die auch
 * `tutorial_rollen()` (Nr. 173) zählt. Für Admin, Super-Admin und Freelancer
 * fragt der Aufrufer gar nicht erst (`lernrollenHaengenAnRollen`).
 *
 * `undefined` heißt „unbekannt“ — keine Id, Tabelle fehlt (Migration offen),
 * Netz, RLS: dann gilt die Systemrolle allein, wie vor dem Rollensystem.
 * Kein Wurf, kein Toast.
 */
export const ladeEigeneRollen = async (userId: string | null | undefined): Promise<string[] | undefined> => {
    if (!userId) return undefined;
    try {
        const { data, error } = await supabase
            .from(T_BENUTZER_ROLLEN)
            .select('rolle')
            .eq('user_id', userId);
        if (error) throw error;
        const raus = new Set<string>();
        (data ?? []).forEach((row: Record<string, unknown>) => {
            const rolle = alsText(row.rolle);
            if (rolle) raus.add(rolle);
        });
        return [...raus].sort();
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Rollen nicht geladen', e);
        return undefined;
    }
};

// ─── Schreiben ───────────────────────────────────────────────────────────────

const aktuelleUserId = async (): Promise<string | null> => {
    try {
        const { data } = await supabase.auth.getUser();
        return data?.user?.id ?? null;
    } catch {
        return null;
    }
};

/**
 * Kapitel geoeffnet. Upsert auf den Primaerschluessel (user_id, kapitel_id) —
 * ein zweiter Besuch schiebt `gesehen_am` nach vorn, loescht aber NIE ein
 * gesetztes `abgeschlossen_am`: die Spalte wird hier bewusst nicht angefasst.
 *
 * Gibt `false` zurueck, wenn nichts geschrieben wurde (Tabelle fehlt, nicht
 * angemeldet, RLS). Der Aufrufer darf das ignorieren.
 */
export const markiereGesehen = async (kapitelId: TutorialKapitelId): Promise<boolean> => {
    const userId = await aktuelleUserId();
    if (!userId) return false;
    try {
        const { error } = await supabase
            .from(T_FORTSCHRITT)
            .upsert({ user_id: userId, kapitel_id: kapitelId, gesehen_am: new Date().toISOString() }, { onConflict: 'user_id,kapitel_id' });
        if (error) throw error;
        return true;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] gesehen nicht gemerkt', e);
        return false;
    }
};

/**
 * Kapitel abgeschlossen. Setzt `abgeschlossen_am` und fuellt `gesehen_am` mit,
 * falls es die Zeile noch nicht gab — wer abschliesst, hat gesehen.
 */
export const markiereAbgeschlossen = async (kapitelId: TutorialKapitelId): Promise<boolean> => {
    const userId = await aktuelleUserId();
    if (!userId) return false;
    const jetzt = new Date().toISOString();
    try {
        const { error } = await supabase
            .from(T_FORTSCHRITT)
            .upsert({ user_id: userId, kapitel_id: kapitelId, gesehen_am: jetzt, abgeschlossen_am: jetzt }, { onConflict: 'user_id,kapitel_id' });
        if (error) throw error;
        return true;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Abschluss nicht gemerkt', e);
        return false;
    }
};

/**
 * Tour abgebrochen. Merkt sich den Schritt, auf dem Schluss war, in
 * `tour_abgebrochen_bei` — Rohstoff fuer die Frage, welches Kapitel die Leute
 * regelmaessig an derselben Stelle wegklicken.
 *
 * `gesehen_am` geht wie bei `markiereGesehen` mit: wer eine Tour startet, hat
 * das Kapitel gesehen — auch wenn er sie nach dem zweiten Schritt schliesst.
 * `abgeschlossen_am` wird NICHT angefasst; ein Abbruch nimmt einen frueheren
 * Abschluss nicht zurueck.
 *
 * Der Index wird auf den Wertebereich des CHECK (0..500, Migration Nr. 100)
 * gestutzt: ein Ausreisser darf die Randnotiz nicht in einen Fehler drehen.
 */
export const markiereTourAbgebrochen = async (kapitelId: TutorialKapitelId, schrittIndex: number): Promise<boolean> => {
    const userId = await aktuelleUserId();
    if (!userId) return false;
    const schritt = Number.isFinite(schrittIndex) ? Math.min(500, Math.max(0, Math.trunc(schrittIndex))) : 0;
    try {
        const { error } = await supabase
            .from(T_FORTSCHRITT)
            .upsert(
                { user_id: userId, kapitel_id: kapitelId, gesehen_am: new Date().toISOString(), tour_abgebrochen_bei: schritt },
                { onConflict: 'user_id,kapitel_id' },
            );
        if (error) throw error;
        return true;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Tour-Abbruch nicht gemerkt', e);
        return false;
    }
};

// ─── Ersteinweisung in die App (Willkommens-Tour, Migration Nr. 102) ─────────

/** Die Spalte, die den Merker traegt — an EINER Stelle, sie steht in drei Aufrufen. */
const P_ERSTEINWEISUNG = 'ersteinweisung_am';

/**
 * Die Ersteinweisung ist erledigt — `profiles.ersteinweisung_am` setzen.
 *
 * Gerufen am ENDE der Willkommens-Tour, gleich ob sie durchlaufen oder
 * weggeklickt wurde: wer sie wegklickt, will sie nicht noch einmal sehen. Wer
 * sie doch nochmal will, startet das Kapitel im Lernzentrum von Hand.
 *
 * Fail-open wie der Rest dieser Datei, und hier mit einem Grund mehr: die
 * Spalte entsteht erst mit Migration Nr. 102. Fehlt sie, antwortet PostgREST
 * mit `42703` — dann bleibt es beim Browser-Merker, und das ist genau das
 * Verhalten von vorher. Rueckgabe ist der Zeitstempel, den der Aufrufer ins
 * Profil im Zustand uebernimmt (sonst startet die Tour im selben Sitzungslauf
 * ein zweites Mal, sobald der naechste Ansichtswechsel den Effekt erneut
 * auswertet), oder `null`, wenn nichts geschrieben wurde.
 *
 * Der Update nennt `eq('id', userId)` ausdruecklich: die Policy
 * `profiles_internal_all` auf `profiles` ist breiter als noetig
 * (`using (true)`), der Client soll deshalb von sich aus nur die eigene Zeile
 * anfassen.
 */
export const merkeErsteinweisung = async (): Promise<string | null> => {
    const userId = await aktuelleUserId();
    if (!userId) return null;
    const jetzt = new Date().toISOString();
    try {
        const { error } = await supabase
            .from('profiles')
            .update({ [P_ERSTEINWEISUNG]: jetzt })
            .eq('id', userId);
        if (error) throw error;
        return jetzt;
    } catch (e) {
        if (!istNochNichtVeroeffentlicht(e)) console.warn('[lernzentrum] Ersteinweisung nicht gemerkt', e);
        return null;
    }
};

/**
 * Ersteinweisung zuruecksetzen — der Nutzer bekommt die Tour erneut.
 *
 * Nur aus der Benutzerverwaltung heraus (Admin). Fremde Zeilen darf der Client
 * technisch schreiben (siehe oben, `profiles_internal_all`); die Beschraenkung
 * auf Administratoren macht die Oberflaeche, nicht die Datenbank — genau wie
 * beim Freischalten und beim Rollenwechsel daneben.
 *
 * NICHT fail-open: das hier ist ein Klick mit erwarteter Wirkung, kein
 * Nebenher-Protokoll. Der Aufrufer bekommt den Fehler und zeigt ihn an.
 */
export const setzeErsteinweisungZurueck = async (userId: string): Promise<void> => {
    const { error } = await supabase
        .from('profiles')
        .update({ [P_ERSTEINWEISUNG]: null })
        .eq('id', userId);
    if (error) throw error;
};
